/* eslint-disable camelcase */
import { GPSTrackMapper, HexColor, Location, LocationMapper } from '@nx-smartmonkey/shared/domain';
import { ROUTE_END_LOCATION, ROUTE_START_LOCATION, ROUTE_STATUS } from '../constants/Routes';
import { Route } from '../domain/route/Route';
import { RouteRepositoryDTO } from '../domain/route/RouteRepositoryDTO';
import { RouteStatus } from '../domain/route/RouteStatus';
import { Stop } from '../domain/stops/Stop';
import { RouteEntry } from '../entries/RouteEntry';
import { StopEntry } from '../entries/StopEntry';
import { RouteSurveyMapper } from './RouteSurveyMapper';
import StopMapper from './StopMapper';

export class RouteMapper {
  static toDomain(props: RouteEntry): Route {
    const {
      id,
      project_id,
      external_id,
      vehicle_id,
      label,
      color,
      phone,
      start_location,
      end_location,
      start_date,
      end_date,
      estimated_end_time,
      status,
      planned_track,
      planned_start_time,
      planned_end_time,
      last_position,
      total_distance,

      tracing_percent,
      distance_traveled,

      stops,
      plan_id,
      plan_label,
      plan_execution_date,
      survey,

      created_at,
      updated_at,
      created_by,
    } = props;

    const surveyDomain = survey ? RouteSurveyMapper.toDomain(survey) : undefined;

    return Route.create(
      {
        project_id,
        external_id,
        vehicle_id,
        label,
        color: color ? HexColor.create(color) : undefined,
        phone,
        start_location: start_location && Location.create(start_location),
        end_location: end_location && Location.create(end_location),
        start_date,
        end_date,
        estimated_end_time,
        status: status as RouteStatus,
        planned_track,
        planned_start_time,
        planned_end_time,
        last_position: last_position ? GPSTrackMapper.toDomain(last_position) : undefined,
        tracing_percent,
        distance_traveled,
        stops: RouteStopsMapper(
          stops,
          status as RouteStatus,
          start_location && Location.create(start_location),
          end_location && Location.create(end_location)
        ),
        plan_id,
        plan_label,
        plan_execution_date,
        survey: surveyDomain,
        total_distance,

        created_at,
        updated_at,
        created_by,
      },
      id
    );
  }

  static toRepository(route: Route): RouteRepositoryDTO {
    return {
      ...(route.label ? { label: route.label } : {}),
      ...(route.plan_execution_date ? { execution_date: route.plan_execution_date } : {}),
      ...(route.start_location ? { start_location: LocationMapper.toDTO(route.start_location) } : {}),
      ...(route.end_location ? { end_location: LocationMapper.toDTO(route.end_location) } : {}),
    };
  }
}

const RouteStopsMapper = (
  stops: StopEntry[],
  status: RouteStatus,
  startLocation?: Location,
  endLocation?: Location
): Stop[] => {
  const stopsResult = [] as Stop[];

  if (startLocation) {
    stopsResult.push(
      Stop.create(
        {
          location: startLocation,
          order: -1,
          tasks: [],
          stop_fields: [],
          status: status !== `not_started` ? `completed` : `pending`,
          isSpecificStop: true,
          type: `delivery`,
        },
        ROUTE_START_LOCATION
      )
    );
  }

  stops.forEach((stop) => {
    stopsResult.push(StopMapper.toDomain(stop));
  });

  if (endLocation) {
    const routeCompleted = stops.every((stop) => stop.status !== `pending`);

    stopsResult.push(
      Stop.create(
        {
          location: endLocation,
          order: -1,
          tasks: [],
          stop_fields: [],
          status: status === ROUTE_STATUS.finished && routeCompleted ? `completed` : `pending`,
          isSpecificStop: true,
          type: `delivery`,
        },
        ROUTE_END_LOCATION
      )
    );
  }

  return stopsResult;
};
